import {
  computed,
  h,
  defineComponent,
  onMounted,
  onUnmounted,
  provide,
  ref,
  VNodeChildren,
} from 'vue';
import { SelectOptions } from '@/ui/types';
import { emptyIssue, IssueRecordEditable } from '@/ui/pages/issue/issueTypes';
import { useTask } from '@/ui/concerns';
import { filter as getAllUsers } from '@/services/api/resources/user';
import { filter as getAllGroups } from '@/services/api/resources/group';
import {
  filterListToOptions,
  gtm,
  sequence,
  toasted,
  filterListToEditableGroups,
  filterListToEditableUsers,
} from '@/ui/helpers';
import { filter as getAllEntityItems } from '@/services/api/resources/entityItem';
import { create, intoSendable } from '@/services/api/resources/issue/v2';
import { user } from '@/ui/auth/auth';
import { t } from '@/ui/i18n/i18n';
import { useIssueForm } from '@/ui/pages/issue/useIssueForm';
import { IssueStatus } from '@/entities/todo';
import { loader, loadingButton } from '@/ui/components';
import { EventBus } from '@/ui/EventBus';
import { Events } from '@/ui/events';
import { useEntityItemSelection } from '@/ui/pages/entityItem/useEntityItemSelection';
import { v4 as uuidv4 } from 'uuid';
import { createUserIdentifierFromActionPageKey } from '@/ui/injectionKeys';
import { CreateUserModelIdentifier } from '@/ui/components/user/CreateUserModal';
import { Userflow, UserflowTrackEvent } from '@/services/userflow/userflow';
import { useAssignees } from '@/ui/concerns/useAssignees';

interface IssueCreateProps {
  hideSendButton?: boolean;
  mergeAttributes?: Partial<IssueRecordEditable>;
}

export const IssueCreate = defineComponent<IssueCreateProps>({
  name: 'IssueCreate',
  props: ['hideSendButton', 'mergeAttributes'],
  setup(props, context) {
    provide(createUserIdentifierFromActionPageKey, CreateUserModelIdentifier.actionCreator);
    const entityItemOptions = ref<SelectOptions>([]);

    // create empty record
    const recordEditable = ref<IssueRecordEditable>({ ...emptyIssue() });

    const {
      showEntityItemSelection,
      shouldSelectEntityItemAutomatically,
      shouldFilterEntityItems,
      filterEntityItemsUserIsAssignedTo,
    } = useEntityItemSelection(user, entityItemOptions);

    // send record
    const sendRecord = useTask(
      () => {
        const editable = props.mergeAttributes
          ? { ...recordEditable.value, ...props.mergeAttributes }
          : recordEditable.value;

        editable.assignees = filterListToEditableUsers(editable.assignees);
        editable.assignedGroups = filterListToEditableGroups(editable.assignedGroups);

        const createTask = create(intoSendable(editable, user.value.timezone));

        gtm.dataLayer().push({
          event: 'issueCreated',
        });

        return createTask;
      },
      response => {
        recordEditable.value = { ...emptyIssue() };
        Userflow.trackEvent(UserflowTrackEvent.ACTION_CREATED);
        context.emit('created', response);
      },
      uuidv4(),
      error => {
        toasted.error(error.message, { singleton: true });

        recordEditable.value = {
          ...recordEditable.value,
          assignedGroups: recordEditable.value.assignedGroups.map(group => `group_${group}`),
          assignees: recordEditable.value.assignees.map(assignee => `user_${assignee}`),
        };
      },
    );

    // form
    const { formFields, handleSubmit, submitIsLoading } = useIssueForm(recordEditable, sendRecord);
    const disabledExternalAssignee = computed(
      () => [IssueStatus.Done, IssueStatus.CantDo].includes(recordEditable.value.status),
    );

    const {
      assigneeOptions,
      fillGroupsOptions,
      fillAssigneeOptions,
      getUsers,
    } = useAssignees();

    // get record
    const getFilters = useTask(
      () => sequence(getAllEntityItems(), getAllUsers(), getAllGroups()),
      ([entityItems, users, groups]) => {
        if (shouldFilterEntityItems()) {
          entityItemOptions.value = filterListToOptions({ data: filterEntityItemsUserIsAssignedTo(entityItems.data) });
        } else {
          entityItemOptions.value = filterListToOptions(entityItems);
        }

        fillGroupsOptions(groups);
        fillAssigneeOptions(users);
      },
    );

    onMounted(() => {
      EventBus.$on(Events.submitCreateIssue, handleSubmit);
    });

    onUnmounted(() => {
      EventBus.$off(Events.submitCreateIssue, handleSubmit);
    });

    if (shouldSelectEntityItemAutomatically()) {
      recordEditable.value.entityItem = user.value.entityItems[0]!.id;
    }

    return function render() {
      return loader('form', getFilters, [
        ...formFields<string>(
          entityItemOptions.value,
          assigneeOptions.value,
          disabledExternalAssignee.value,
          showEntityItemSelection.value,
          getUsers,
        ),
        props.hideSendButton
          ? null
          : loadingButton(
            t('issues.create'),
            handleSubmit,
            submitIsLoading.value,
          ),
      ]);
    };
  },
});

export function issueCreate(props: IssueCreateProps, on: Record<string, Function>, children: VNodeChildren = []) {
  return h(IssueCreate, { props, on }, children);
}
